import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Loader2, Save, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { id: 0, label: 'Monday' },
  { id: 1, label: 'Tuesday' },
  { id: 2, label: 'Wednesday' },
  { id: 3, label: 'Thursday' },
  { id: 4, label: 'Friday' },
  { id: 5, label: 'Saturday' },
  { id: 6, label: 'Sunday' },
];

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

/**
 * Recurring weekly digest control panel. Reads / writes the
 * `settings.digest` doc via the backend. A separate poller wakes up
 * once every 5 minutes to queue the campaign - no cron dependency.
 */
const DigestConfigPanel = ({ token, onGenerated }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns/digest/config`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setCfg({
        enabled: !!data.enabled,
        day_of_week: data.day_of_week ?? 0,
        hour: data.hour ?? 7,
        minute: data.minute ?? 0,
        next_run_at: data.next_run_at,
        preview_article_count: data.preview_article_count ?? 0,
        preview_articles: data.preview_articles || [],
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns/digest/config`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          enabled: cfg.enabled,
          day_of_week: cfg.day_of_week,
          hour: cfg.hour,
          minute: cfg.minute,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setCfg((c) => ({ ...c, ...data, enabled: !!data.enabled }));
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const generateNow = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/email-campaigns/digest/generate-now`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      onGenerated?.(data);
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4 text-xs text-gray-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin inline-block mr-2" />
        Loading digest schedule…
      </div>
    );
  }

  return (
    <div
      data-testid="digest-config-panel"
      className="rounded-lg border border-yellow-900/40 bg-gradient-to-br from-yellow-950/20 via-black to-zinc-950 p-4 sm:p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="h-4 w-4 text-yellow-400" />
        <p className="text-[10px] font-black tracking-[0.3em] uppercase text-yellow-400">
          Weekly digest
        </p>
      </div>
      <p className="text-xs text-gray-400 mb-3 leading-relaxed">
        articles and queues it for a fixed time slot. Runs even when you&apos;re asleep - the
        poller queues it up to 24 hours before, and the existing scheduled-send worker fires it.
      </p>

      <div className="grid sm:grid-cols-[auto_1fr_auto_auto] gap-2 items-end mb-3">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            data-testid="digest-enabled-toggle"
            className="h-4 w-4 accent-yellow-500"
          />
          <span className="text-[11px] font-black tracking-widest uppercase text-white">
            {cfg.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
        <select
          value={cfg.day_of_week}
          onChange={(e) => setCfg({ ...cfg, day_of_week: Number(e.target.value) })}
          data-testid="digest-day"
          className="bg-zinc-950 border border-zinc-800 focus:border-yellow-500/60 rounded px-3 py-2 text-sm text-white outline-none"
        >
          {DAYS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select
          value={cfg.hour}
          onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
          data-testid="digest-hour"
          className="bg-zinc-950 border border-zinc-800 focus:border-yellow-500/60 rounded px-3 py-2 text-sm text-white outline-none"
        >
          {Array.from({ length: 24 }, (_, i) => (
            <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
          ))}
        </select>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="digest-save-btn"
          className="inline-flex items-center gap-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-black text-xs font-black uppercase tracking-widest px-4 py-2 rounded"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </button>
      </div>

      <p className="text-[11px] text-gray-400 leading-snug">
        Time is Melbourne local. Currently{' '}
        <strong className="text-white">
          {cfg.enabled ? `every ${DAYS[cfg.day_of_week].label} at ${String(cfg.hour).padStart(2, '0')}:00` : 'disabled'}
        </strong>.
        {cfg.enabled && cfg.next_run_at && (
          <> Next digest fires <strong className="text-yellow-300">{fmt(cfg.next_run_at)}</strong>.</>
        )}
      </p>
      <p className="text-[11px] text-gray-500 mt-1">
        {cfg.preview_article_count > 0
          ? <>Right now the digest would include <strong className="text-white">{cfg.preview_article_count}</strong> article{cfg.preview_article_count === 1 ? '' : 's'} from the last 7 days.</>
          : <>No articles published in the last 7 days - the digest would be skipped this week.</>}
      </p>

      {error && (
        <p className="mt-2 text-xs text-red-300" data-testid="digest-error">{error}</p>
      )}

      <button
        type="button"
        onClick={generateNow}
        disabled={generating || cfg.preview_article_count === 0}
        data-testid="digest-generate-now"
        className="mt-3 inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded"
        title="Build a draft right now so you can preview + edit before firing"
      >
        {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        Generate preview draft now
      </button>
    </div>
  );
};

export default DigestConfigPanel;
